import { useCategory, useCreateCategory, useUpdateCategory, useDeleteCategory } from '@/services/categories';
import { CategoryType } from '@/models/category';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { useEffect, useState } from 'react';
import { errorBannerStyle } from '@/shared/dark-inline-styles';

interface CategoryModalProps {
    isOpen: boolean;
    selectedCategoryId?: string;
    toggle: () => void;
}

const CategoryModal = ({ isOpen, toggle, selectedCategoryId }: CategoryModalProps) => {
    const { data: category, isLoading } = useCategory(selectedCategoryId as string, isOpen);
    const { mutateAsync: createCategory, isPending: isCreating } = useCreateCategory();
    const { mutateAsync: updateCategory, isPending: isUpdating } = useUpdateCategory();
    const { mutateAsync: deleteCategory, isPending: isDeleting } = useDeleteCategory();

    const [name, setName] = useState('');
    // Single type — required. New categories default to Content (the most common);
    // the admin picks Course / Blog when needed. Legacy untyped rows load as Content.
    const [type, setType] = useState<CategoryType>('content');
    // Hidden retires the category (tab + content) from the App's LEARN page without
    // deleting it — flip it back to Visible any time.
    const [hidden, setHidden] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // `isOpen` in the deps so re-opening the SAME row re-fills from cached data
    // (the close-reset below would otherwise leave it blank).
    useEffect(() => {
        if (!isOpen) return;
        if (category?.data) {
            setName(category.data.name);
            setType(category.data.type ?? 'content');
            setHidden(!!category.data.hidden);
            setError(null);
        } else if (!selectedCategoryId) {
            resetStates();
        }
    }, [isOpen, category?.data, selectedCategoryId]);

    useEffect(() => {
        if (!isOpen) {
            resetStates();
        }
    }, [isOpen]);

    const resetStates = () => {
        setName('');
        setType('content');
        setHidden(false);
        setError(null);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Name is required');
            return;
        }
        // callApi never throws on API errors — it resolves { status:'error' }, so
        // a bare mutate would close as "success" on a 4xx/5xx. Inspect the result
        // and surface the backend message instead.
        const result = selectedCategoryId
            ? await updateCategory({ categoryId: selectedCategoryId, name: name.trim(), type, hidden })
            : await createCategory({ name: name.trim(), type, hidden });
        if (result.status === 'error') {
            setError(result.error?.message ?? 'Failed to save the category.');
            return;
        }
        toggle();
    };

    const handleDelete = async () => {
        if (!selectedCategoryId) return;
        const result = await deleteCategory({ categoryId: selectedCategoryId });
        if (result.status === 'error') {
            setError(result.error?.message ?? 'Failed to delete the category.');
            return;
        }
        toggle();
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!selectedCategoryId}
            title={`${selectedCategoryId ? 'Edit' : 'Add'} Category`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <div style={errorBannerStyle}>{error}</div>}
                    <MaterialTextField label="Name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                    <MaterialTextField name="select" select label="Type" value={type} onChange={(e) => setType(e.target.value as CategoryType)}>
                        <MaterialFormOption value="content">Content — App discovery &amp; breathwork</MaterialFormOption>
                        <MaterialFormOption value="course">Course</MaterialFormOption>
                        <MaterialFormOption value="blog">Blog</MaterialFormOption>
                    </MaterialTextField>
                    <p style={{ margin: '-4px 0 6px', fontSize: 12, color: '#999999' }}>
                        Each category belongs to ONE surface. The same name can exist under different types.
                    </p>
                    <MaterialTextField
                        name="select"
                        select
                        label="Visibility"
                        value={hidden ? 'hidden' : 'visible'}
                        onChange={(e) => setHidden(e.target.value === 'hidden')}
                    >
                        <MaterialFormOption value="visible">Visible in LEARN</MaterialFormOption>
                        <MaterialFormOption value="hidden">Hidden — retired from LEARN (tab + content)</MaterialFormOption>
                    </MaterialTextField>
                    <p style={{ margin: '-4px 0 6px', fontSize: 12, color: '#999999' }}>
                        Hidden categories (Content type) disappear from the App’s LEARN page — tab and all content — without being deleted. Flip back to
                        Visible any time.
                    </p>
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isCreating || isUpdating || isDeleting}
        />
    );
};

export default CategoryModal;
